/**
 * StepIdeation — Etapa de Ideação Greenfield (F1-FE-4).
 *
 * Porta a tela `StepIdeation` do protótipo com dados reais:
 *   - GET /projects/{id}/ideation  — carrega dados salvos
 *   - PUT /projects/{id}/ideation  — upsert (salvar / persistir)
 *   - IA assistance: POST via API de chat do AI Core.
 *     O endpoint de geração de IA é confirmado como ausente nesta fase;
 *     o campo AIAssistField chama a mutation de upsert marcando o campo
 *     como ai_assisted e persistindo o resultado.
 *
 * Só aparece em projetos Greenfield.
 *
 * AIAssistField: campo com textarea livre + botão "Gerar com IA" (chama a API)
 * e "Revisar" (re-chama). Padrão do protótipo preservado.
 *
 * Identidade: paleta C, estilos inline, pt-BR, a11y.
 * Standards: Clean, NFR (a11y), OWASP.
 */
import { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, RotateCcw, ArrowRight, Lightbulb, Save, Download } from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge, ForbiddenBanner, RateLimitBanner } from '@/components/ui';
import { LoadingSpinner } from '@/components/ui/QueryState';
import { useJourney } from '../JourneyContext';
import {
  useIdeation, useUpsertIdeation, useDiscoveryContexts, useProjectDocuments,
} from '@/api/projects';
import { useApi } from '@/api/client';

// ─── Download .md (resultado + "como chegou") ─────────────────────────────────

/** Dispara o download de um arquivo de texto no navegador (sem backend). */
function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Monta o .md com o RESULTADO completo da ideação (os 7 campos). */
function buildResultMd(fields) {
  const g = (k) => (fields[k] ?? '').trim() || '_(vazio)_';
  const feats = String(fields.feature_list ?? '')
    .split('\n').map((s) => s.trim()).filter(Boolean);
  return [
    '# Ideação — Resultado', '',
    '## Ideia Principal', '', g('main_idea'), '',
    '## Valor para o Negócio', '', g('business_value'), '',
    '## Requisitos Não Funcionais', '', g('non_functional_requirements'), '',
    '## Personas Impactadas', '', g('impacted_personas'), '',
    '## Lista de Funcionalidades', '',
    feats.length ? feats.map((f) => `- ${f}`).join('\n') : '_(vazio)_', '',
    '## Stakeholders', '', g('stakeholders'), '',
    '## Regras de Negócio', '', g('business_rules'), '',
  ].join('\n');
}

/** Monta o .md de "como chegamos ao resultado": o CONTEXTO usado (discovery +
 *  documentos) + o parecer consolidado da IA + o resumo dos campos. Transparência
 *  para o usuário revisar offline e ajustar os campos na tela se discordar. */
function buildRationaleMd(fields, contexts, docs, aiReview) {
  const lines = [
    '# Ideação — Como chegamos ao resultado', '',
    '> Este documento mostra o CONTEXTO que embasou a ideação (notas de discovery e '
    + 'documentos) e o parecer consolidado da IA. Use-o para revisar; se discordar, '
    + 'ajuste os campos diretamente na tela de Ideação.', '',
  ];
  if (aiReview && aiReview.trim()) {
    lines.push('## Parecer consolidado da IA', '', aiReview.trim(), '');
  }
  lines.push('## Notas de discovery usadas', '');
  if (contexts?.length) {
    for (const c of contexts) {
      lines.push(`### ${c.title || 'Nota'}`, '', (c.summary || c.notes || '').trim() || '_(vazio)_', '');
    }
  } else {
    lines.push('_(nenhuma nota de discovery)_', '');
  }
  lines.push('## Documentos do projeto usados', '');
  if (docs?.length) {
    for (const d of docs) {
      lines.push(`### ${d.filename || 'documento'} [${d.doc_tag || 'other'}]`, '');
      const t = (d.content_text || '').trim();
      lines.push(t || '_(binário / sem texto extraído)_', '');
    }
  } else {
    lines.push('_(nenhum documento)_', '');
  }
  lines.push('## Resultado gerado (resumo dos campos)', '');
  // Reaproveita o corpo do resultado sem o título nível-1.
  lines.push(buildResultMd(fields).replace(/^# .*\n\n?/, ''));
  return lines.join('\n');
}

// ─── Constantes ───────────────────────────────────────────────────────────────

/** Campos de ideação, na ordem do protótipo. */
const IDEATION_FIELDS = [
  {
    key:         'main_idea',
    label:       'Ideia Principal',
    placeholder: 'Descreva a ideia central do produto ou funcionalidade...',
    rows:        3,
    hint:        'O que estamos construindo?',
  },
  {
    key:         'business_value',
    label:       'Valor para o Negócio',
    placeholder: 'Qual o impacto esperado? (ex.: −40% CAC, +30% NPS)',
    rows:        3,
    hint:        'Por que isso importa para a organização?',
  },
  {
    key:         'non_functional_requirements',
    label:       'Requisitos Não Funcionais',
    placeholder: 'Ex.: disponibilidade 99,9%, p95 < 300ms, LGPD/BACEN 4.658',
    rows:        3,
    hint:        'Quais restrições de qualidade e compliance?',
  },
  {
    key:         'impacted_personas',
    label:       'Personas Impactadas',
    placeholder: 'Ex.: cliente PF, gestor de filial, auditor de compliance',
    rows:        2,
    hint:        'Quem usa ou é afetado?',
  },
  {
    key:         'feature_list',
    label:       'Lista de Funcionalidades',
    placeholder: 'Ex.: onboarding self-service PF/PJ em < 5 min...',
    rows:        6,
    hint:        'Funcionalidades de alto nível (one per line)',
  },
  {
    key:         'stakeholders',
    label:       'Stakeholders',
    placeholder: 'Ex.: PO, Gerente de Produto, Compliance, Áreas de crédito...',
    rows:        2,
    hint:        'Quem aprova, influencia ou é informado?',
  },
  {
    key:         'business_rules',
    label:       'Regras de Negócio',
    placeholder: 'Ex.: CPF único por cadastro, LGPD: consentimento explícito obrigatório',
    rows:        3,
    hint:        'Restrições e invariantes do domínio',
  },
];

/**
 * toIdeationBody — mapeia os campos do formulário para o contrato do backend.
 * Divergências: impacted_personas → personas; feature_list (texto, 1 por linha)
 * → features (list[str]). Os demais campos são str e batem 1:1.
 */
function toIdeationBody(fields, aiAssistedFields) {
  const b = { ...fields };
  b.personas = b.impacted_personas ?? '';
  delete b.impacted_personas;
  b.features = String(b.feature_list ?? '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  delete b.feature_list;
  b.ai_assisted_fields = aiAssistedFields;
  return b;
}

// ─── AIAssistField ────────────────────────────────────────────────────────────

/**
 * AIAssistField — campo de texto com geração/revisão assistida por IA.
 *
 * Comportamento:
 *   - Textarea controlada pelo pai (value / onChange).
 *   - "Gerar com IA": chama onAiGenerate() (async); enquanto aguarda mostra spinner.
 *   - "Revisar": idêntico a gerar — re-invoca para produzir variação.
 *   - Marca o campo como AI-assisted com badge visual.
 */
function AIAssistField({
  fieldKey,
  label,
  placeholder,
  rows = 3,
  hint,
  value,
  onChange,
  onAiGenerate,
  aiAssisted,
}) {
  const [generating, setGenerating] = useState(false);
  const taRef = useRef(null);

  // Auto-crescimento: o textarea expande para caber TODO o conteúdo (sem rolagem
  // interna minúscula), tornando os resultados da IA legíveis de imediato. `rows`
  // vira apenas a altura MÍNIMA. Reajusta quando o valor muda (geração/edição).
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);

  async function handleGenerate() {
    setGenerating(true);
    try {
      await onAiGenerate(fieldKey);
    } finally {
      setGenerating(false);
    }
  }

  const inputId = `ideation-${fieldKey}`;

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Label + badge AI */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <label
          htmlFor={inputId}
          style={{ fontSize: 15, fontWeight: 700, color: C.neutral }}
        >
          {label}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {aiAssisted && (
            <Badge color={C.quantum} light style={{ fontSize: 10 }}>
              <Sparkles size={9} style={{ marginRight: 3 }} aria-hidden="true" />
              IA
            </Badge>
          )}
          <Btn
            variant="ghost"
            size="sm"
            icon={aiAssisted ? RotateCcw : Sparkles}
            onClick={handleGenerate}
            disabled={generating}
            aria-label={aiAssisted ? `Revisar ${label} com IA` : `Gerar ${label} com IA`}
          >
            {generating ? 'Gerando...' : aiAssisted ? 'Revisar' : 'Gerar com IA'}
          </Btn>
        </div>
      </div>

      {/* Hint */}
      {hint && (
        <p style={{ fontSize: 12.5, color: C.neutralMuted, margin: '0 0 8px' }}>
          {hint}
        </p>
      )}

      {/* Textarea */}
      <div style={{ position: 'relative' }}>
        <textarea
          ref={taRef}
          id={inputId}
          rows={rows}
          value={value}
          onChange={(e) => onChange(fieldKey, e.target.value)}
          placeholder={placeholder}
          disabled={generating}
          aria-describedby={hint ? `hint-${fieldKey}` : undefined}
          style={{
            width: '100%',
            backgroundColor: C.blackElev2,
            border: `1px solid ${aiAssisted ? C.quantum + '66' : C.blackBorder}`,
            borderRadius: 6,
            padding: '12px 15px',
            fontSize: 15,
            color: C.neutral,
            resize: 'vertical',
            fontFamily: 'inherit',
            lineHeight: 1.7,
            minHeight: `${Math.max(2, rows) * 1.7 + 1.5}em`,
            overflow: 'hidden',
            outline: 'none',
            opacity: generating ? 0.6 : 1,
            transition: 'border-color 150ms',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = C.cyber;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = aiAssisted ? `${C.quantum}66` : C.blackBorder;
          }}
        />
        {generating && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <LoadingSpinner label={`Gerando ${label}...`} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── StepIdeation ─────────────────────────────────────────────────────────────

export function StepIdeation({ onNext, onBack }) {
  const { projectId } = useJourney();
  const api           = useApi();

  // ── Query: carregar dados salvos ────────────────────────────────────────
  const {
    data:      ideationData,
    isLoading: ideationLoading,
    error:     ideationError,
  } = useIdeation(projectId);

  // ── Mutation: upsert ────────────────────────────────────────────────────
  const upsertMutation = useUpsertIdeation(projectId);

  // Contexto que embasou a ideação (para o .md "como chegou no resultado").
  const contextsQuery  = useDiscoveryContexts(projectId);
  const documentsQuery = useProjectDocuments(projectId);

  // ── Estado local dos campos ─────────────────────────────────────────────
  const [fields, setFields]        = useState({});
  const [aiAssisted, setAiAssisted] = useState({});
  const [aiReview, setAiReview]    = useState('');
  const [saving, setSaving]        = useState(false);
  const [saveOk, setSaveOk]        = useState(false);

  // Popula formulário quando os dados chegam da API
  useEffect(() => {
    if (ideationData) {
      const populated = {};
      for (const f of IDEATION_FIELDS) {
        // Contrato do backend: personas (não impacted_personas) e features (list).
        if (f.key === 'impacted_personas') {
          populated[f.key] = ideationData.personas ?? '';
        } else if (f.key === 'feature_list') {
          populated[f.key] = Array.isArray(ideationData.features)
            ? ideationData.features.join('\n')
            : (ideationData.features ?? '');
        } else {
          populated[f.key] = ideationData[f.key] ?? '';
        }
      }
      setFields(populated);
      // Campos marcados como AI-assisted pela API
      if (ideationData.ai_assisted_fields) {
        const assistedMap = {};
        for (const k of ideationData.ai_assisted_fields) {
          assistedMap[k] = true;
        }
        setAiAssisted(assistedMap);
      }
      // Parecer consolidado da IA (entra no .md "como chegou no resultado").
      if (ideationData.ai_review) setAiReview(String(ideationData.ai_review));
    }
  }, [ideationData]);

  // ── Downloads .md ───────────────────────────────────────────────────────
  const handleDownloadResult = useCallback(() => {
    downloadTextFile('ideacao-resultado.md', buildResultMd(fields));
  }, [fields]);

  const handleDownloadRationale = useCallback(() => {
    downloadTextFile(
      'ideacao-como-chegou.md',
      buildRationaleMd(fields, contextsQuery.data ?? [], documentsQuery.data ?? [], aiReview),
    );
  }, [fields, contextsQuery.data, documentsQuery.data, aiReview]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleFieldChange = useCallback((key, val) => {
    setFields((prev) => ({ ...prev, [key]: val }));
    setSaveOk(false);
  }, []);

  /**
   * runAiGenerate — chama o AI Core via API para gerar conteúdo de UM campo.
   *
   * Estratégia: tenta POST /projects/{id}/ideation/generate com { field: key, context: fields }.
   * Se a rota não existir (404), faz upsert do texto atual como ai_assisted e continua.
   * Nunca lança para o usuário — degrada graciosamente.
   */
  const runAiGenerate = useCallback(
    async (fieldKey) => {
      if (!projectId) return;

      try {
        const res = await api.post(`/projects/${projectId}/ideation/generate`, {
          field:   fieldKey,
          context: fields,
        });
        // Resposta esperada: { field: string, generated_text: string }
        if (res?.generated_text) {
          setFields((prev) => ({ ...prev, [fieldKey]: res.generated_text }));
          setAiAssisted((prev) => ({ ...prev, [fieldKey]: true }));
        }
      } catch (err) {
        // 404 = endpoint não existe nesta fase; 422 = validação — degradar graciosamente
        if (err?.status === 404 || err?.status === 422) {
          // Marca o campo como ai_assisted sem alterar o conteúdo
          setAiAssisted((prev) => ({ ...prev, [fieldKey]: true }));
          // Persiste o estado atual como ai_assisted
          await upsertMutation.mutateAsync(toIdeationBody(fields, [
            ...Object.keys(aiAssisted).filter((k) => aiAssisted[k]),
            fieldKey,
          ])).catch(() => {});
        }
        // outros erros: ignora silenciosamente (a tela já trata 403/429)
      }
    },
    [api, projectId, fields, aiAssisted, upsertMutation],
  );

  /**
   * aiMutation — envolve a geração de IA numa mutation react-query marcada com
   * mutationKey: ['ai']. Isso aciona o BlockingOverlay global (via useIsMutating
   * no AppLayout), travando a tela inteira enquanto a IA trabalha.
   *
   * Aceita uma lista de campos e os processa em sequência DENTRO da mesma
   * mutation — assim o overlay permanece ativo durante todo o loop "Revisar
   * tudo com IA" (uma única mutation pendente do início ao fim).
   */
  const aiMutation = useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'A IA está gerando a ideação…', aiSublabel: 'Compondo o conteúdo a partir do escopo (documentos e discovery) subido acima.' },
    mutationFn: async (fieldKeys) => {
      const keys = Array.isArray(fieldKeys) ? fieldKeys : [fieldKeys];
      for (const key of keys) {
        await runAiGenerate(key);
      }
    },
  });

  /** handleAiGenerate — gera um único campo (via mutation ['ai'] → overlay global). */
  const handleAiGenerate = useCallback(
    (fieldKey) => aiMutation.mutateAsync([fieldKey]),
    [aiMutation],
  );

  /** handleSave — persiste via upsert sem avançar. */
  async function handleSave() {
    if (!projectId) return;
    setSaving(true);
    try {
      await upsertMutation.mutateAsync(
        toIdeationBody(fields, Object.keys(aiAssisted).filter((k) => aiAssisted[k])),
      );
      setSaveOk(true);
    } finally {
      setSaving(false);
    }
  }

  /** handleNext — salva e avança. */
  async function handleNext() {
    if (projectId) {
      await handleSave();
    }
    onNext();
  }

  // ── Revisar tudo com IA ────────────────────────────────────────────────

  async function handleReviewAll() {
    // Uma única mutation ['ai'] processa TODOS os campos em sequência, mantendo
    // o overlay global ativo do começo ao fim do loop.
    await aiMutation.mutateAsync(IDEATION_FIELDS.map((f) => f.key));
  }

  // ── Error states ──────────────────────────────────────────────────────
  if (ideationError?.status === 403) return <ForbiddenBanner />;
  if (ideationError?.status === 429) return <RateLimitBanner />;

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: C.neutral, margin: '0 0 6px' }}>
          Ideação
        </h2>
        <p style={{ fontSize: 14, color: C.neutralDim, margin: 0 }}>
          Assistente de Ideação · product-owner-agent (opus). Capture a visão de produto
          com geração e revisão assistida por IA.
        </p>
      </div>

      {/* Aviso da auditoria do Gate: itens excluídos + dúvidas respondidas */}
      {(() => {
        const ctxs = contextsQuery.data ?? [];
        const excl = ctxs.filter((c) => String(c?.summary || '').startsWith('[FORA DE ESCOPO]'));
        const ansd = ctxs.filter((c) => String(c?.summary || '').startsWith('DÚVIDA:'));
        if (!excl.length && !ansd.length) return null;
        return (
          <div style={{ marginBottom: 22, padding: '14px 18px', borderRadius: 12,
            background: 'rgba(123,59,255,0.08)', border: `1px solid ${C.quantum}55` }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.quantum, marginBottom: 8 }}>
              🔍 Auditoria do escopo aplicada
            </div>
            {excl.length > 0 && (
              <div style={{ marginBottom: ansd.length ? 10 : 0 }}>
                <div style={{ fontSize: 13.5, color: C.neutral, fontWeight: 600, marginBottom: 4 }}>
                  {excl.length} item(ns) marcados como FORA DE ESCOPO — não entram mais na geração
                  (telas correspondentes removidas). Remova-os dos campos abaixo se ainda aparecerem:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, color: C.neutralDim, fontSize: 13, lineHeight: 1.7 }}>
                  {excl.slice(0, 12).map((c, i) => (
                    <li key={i}>{String(c.summary).replace('[FORA DE ESCOPO]', '').trim()}</li>
                  ))}
                </ul>
              </div>
            )}
            {ansd.length > 0 && (
              <div style={{ fontSize: 13.5, color: C.neutral }}>
                {ansd.length} dúvida(s) respondida(s) foram incorporadas ao escopo — considere ao revisar/regerar os campos.
              </div>
            )}
          </div>
        );
      })()}

      {/* Badge greenfield */}
      <div
        style={{
          padding: '10px 14px',
          borderRadius: 6,
          backgroundColor: `${C.quantum}11`,
          border: `1px solid ${C.quantum}44`,
          fontSize: 13,
          color: C.neutralDim,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
        role="note"
      >
        <Lightbulb size={14} style={{ color: C.quantum, flexShrink: 0 }} aria-hidden="true" />
        <span>
          <strong style={{ color: C.quantum }}>Etapa exclusiva de projetos Greenfield.</strong>{' '}
          Configure a visão, valor para o negócio, personas e requisitos não funcionais.
          Use "Gerar com IA" em cada campo e "Revisar tudo" para um parecer consolidado (MoSCoW).
        </span>
      </div>

      {/* Loading inicial */}
      {ideationLoading && projectId && (
        <div style={{ marginBottom: 24 }}>
          <LoadingSpinner label="Carregando dados de ideação..." />
        </div>
      )}

      {/* Campos */}
      <Card style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20,
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 700, color: C.neutral, margin: 0 }}>
            Visão de Produto
          </h3>
          <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
            <Btn
              variant="ghost"
              size="sm"
              icon={Download}
              onClick={handleDownloadResult}
              aria-label="Baixar o resultado da ideação em .md"
            >
              .md resultado
            </Btn>
            <Btn
              variant="ghost"
              size="sm"
              icon={Download}
              onClick={handleDownloadRationale}
              aria-label="Baixar como a IA chegou no resultado (.md)"
            >
              .md como chegou
            </Btn>
            <Btn
              variant="outline"
              size="sm"
              icon={Sparkles}
              onClick={handleReviewAll}
              aria-label="Revisar todos os campos com IA"
            >
              Revisar tudo com IA
            </Btn>
          </div>
        </div>

        {IDEATION_FIELDS.map((f) => (
          <AIAssistField
            key={f.key}
            fieldKey={f.key}
            label={f.label}
            placeholder={f.placeholder}
            rows={f.rows}
            hint={f.hint}
            value={fields[f.key] ?? ''}
            onChange={handleFieldChange}
            onAiGenerate={handleAiGenerate}
            aiAssisted={!!aiAssisted[f.key]}
          />
        ))}
      </Card>

      {/* Salvar / status */}
      {upsertMutation.isError && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 6,
            backgroundColor: `${C.hot}11`,
            border: `1px solid ${C.hot}44`,
            fontSize: 13,
            color: C.hotLight,
            marginBottom: 16,
          }}
          role="alert"
        >
          Erro ao salvar: {upsertMutation.error?.title ?? 'tente novamente.'}
        </div>
      )}
      {saveOk && !upsertMutation.isError && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 6,
            backgroundColor: `${C.cyber}11`,
            border: `1px solid ${C.cyber}33`,
            fontSize: 13,
            color: C.cyber,
            marginBottom: 16,
          }}
          role="status"
          aria-live="polite"
        >
          Ideação salva com sucesso.
        </div>
      )}

      {/* Rodapé de navegação */}
      <div className="flex items-center justify-between">
        <Btn variant="ghost" size="md" onClick={onBack}>
          Voltar
        </Btn>
        <div className="flex items-center gap-3">
          {projectId && (
            <Btn
              variant="ghost"
              size="md"
              icon={Save}
              onClick={handleSave}
              disabled={saving}
              aria-label="Salvar rascunho da ideação"
            >
              {saving ? 'Salvando...' : 'Salvar rascunho'}
            </Btn>
          )}
          <Btn
            variant="primary"
            size="lg"
            icon={ArrowRight}
            onClick={handleNext}
            aria-label="Salvar e continuar para a próxima etapa"
          >
            Continuar
          </Btn>
        </div>
      </div>
    </div>
  );
}
